import {
	type GeneratedArtifact,
	GeneratedPodcastStageStatus,
} from '@kimuno/api-sdk/lib/types/responses/Ai';
import type { ConversationListItem } from '@kimuno/api-sdk/lib/types/responses/Conversation';

import { AsyncActionStatus } from '../actions/common.actionTypes';
import { AiLoadingStatus } from './aiAssistant.reducers';
import type { StoreType } from './index';

export const getConversation = (state: StoreType, conversationId: string | null) =>
	conversationId ? state.aiAssistant.conversations.byId[conversationId] : null;

export const getCurrentConversationId = (state: StoreType) =>
	state.aiAssistant.conversations.currentConversationId;

export const getTemporaryConversation = (state: StoreType) =>
	state.aiAssistant.conversations.temporaryConversation;

export const getCurrentConversation = (state: StoreType) =>
	getConversation(state, getCurrentConversationId(state)) || getTemporaryConversation(state);

export const getConversationStream = (state: StoreType) => state.aiAssistant.conversations.stream;

export const getConversationDocument = (state: StoreType) =>
	state.aiAssistant.conversations.stream.document;

export const getConversationStreamActiveToolName = (state: StoreType) =>
	state.aiAssistant.conversations.stream.activeToolName;

export const getGeneratedPodcastId = (state: StoreType) =>
	state.aiAssistant.conversations.stream.generatedPodcastId;

export const getDocumentById = (state: StoreType, documentId: string | null) =>
	documentId ? state.aiAssistant.conversations.documents.byId[documentId] : undefined;

export const getActiveDocumentId = (state: StoreType) =>
	state.aiAssistant.conversations.documents.activeDocumentId;

export const getActiveDocument = (state: StoreType) =>
	getDocumentById(state, getActiveDocumentId(state));

export const getActiveDocumentData = (state: StoreType) => getActiveDocument(state)?.data;

export const isActiveDocumentLoading = (state: StoreType) =>
	getActiveDocument(state)?.status === AsyncActionStatus.LOADING;

export const isDocumentLoading = (state: StoreType, documentId: string | null) =>
	getDocumentById(state, documentId)?.status === AsyncActionStatus.LOADING;

export const getPodcastById = (state: StoreType, podcastId: string | null) =>
	podcastId ? state.aiAssistant.conversations.podcasts?.byId?.[podcastId] : undefined;

export const getActivePodcastId = (state: StoreType) =>
	state.aiAssistant.conversations.podcasts?.activePodcastId ?? null;

export const getActivePodcast = (state: StoreType) =>
	getPodcastById(state, getActivePodcastId(state));

export const isPodcastLoading = (state: StoreType, podcastId: string | null) =>
	getPodcastById(state, podcastId)?.status === AsyncActionStatus.LOADING;

// The podcast currently generating in the current conversation, or null. Read
// from the podcast records themselves (not the active view), so it holds in
// the chat view for podcasts generated or refined mid-chat.
export const getGeneratingPodcastIdForCurrentConversation = (state: StoreType) => {
	const messages = getCurrentConversation(state)?.data?.messages ?? [];
	for (const message of messages) {
		if (message.role !== 'assistant') continue;
		const podcastId = (message.context as { generatedPodcastId?: string } | undefined)
			?.generatedPodcastId;
		if (
			podcastId &&
			getPodcastById(state, podcastId)?.data?.status === GeneratedPodcastStageStatus.LOADING
		) {
			return podcastId;
		}
	}
	return null;
};

export const getConversationsList = (state: StoreType) => state.aiAssistant.conversations.list;

export const getConversationsListPagination = (state: StoreType) =>
	getConversationsList(state).pagination;

export const getConversationsLoadingState = (state: StoreType) => {
	const pages = state.aiAssistant.conversations.list.pages;

	if (pages.length === 0) {
		return AiLoadingStatus.NONE;
	}
	if (pages[0].status === AsyncActionStatus.LOADING) {
		return AiLoadingStatus.INITIAL;
	}
	if (pages[pages.length - 1].status === AsyncActionStatus.LOADING) {
		return AiLoadingStatus.PAGINATION;
	}
	return AiLoadingStatus.LOADED;
};

export const isConversationListPageLoading = (state: StoreType) => {
	const pages = state.aiAssistant.conversations.list.pages;
	const pageStatus = pages.map((page) => page.status);
	return pageStatus.some((status) => status === AsyncActionStatus.LOADING);
};

export const getConversationsListResults = (state: StoreType): ConversationListItem[] => {
	const pages = state.aiAssistant.conversations.list.pages;

	return pages.reduce((listResults, page) => {
		const pageResults =
			page.data && page.data.map((id) => state.aiAssistant.conversations.list.byId[id]);

		if (!pageResults) {
			return listResults;
		}

		return listResults.concat(pageResults as ConversationListItem[]);
	}, [] as ConversationListItem[]);
};

export const getSuggestions = (state: StoreType, requestId: string) =>
	state.aiAssistant.suggestions[requestId];

export const getSuggestionsData = (state: StoreType, requestId: string) =>
	getSuggestions(state, requestId)?.data || [];

export const getSuggestionsStatus = (state: StoreType, requestId: string) =>
	getSuggestions(state, requestId)?.status || AsyncActionStatus.INITIAL;

export const isSuggestionsLoading = (state: StoreType, requestId: string) =>
	getSuggestionsStatus(state, requestId) === AsyncActionStatus.LOADING;

export const isSuggestionsReady = (state: StoreType, requestId: string) =>
	getSuggestionsStatus(state, requestId) === AsyncActionStatus.READY;

export const isSuggestionsError = (state: StoreType, requestId: string) =>
	getSuggestionsStatus(state, requestId) === AsyncActionStatus.ERROR;

export const getAttachment = (state: StoreType, attachmentHashName: string) =>
	state.aiAssistant.conversations.attachments.byId[attachmentHashName];

export const getAttachmentStatus = (state: StoreType, attachmentHashName: string) =>
	getAttachment(state, attachmentHashName)?.status || AsyncActionStatus.INITIAL;

export const getTemporaryAttachments = (state: StoreType) =>
	state.aiAssistant.conversations.attachments.temporary;

export const getAttachmentsRemainingLimit = (state: StoreType) =>
	state.aiAssistant.conversations.attachments.remainingLimit.data;

// An upload in flight occupies `temporary` under a client-side uuid until the
// server hash replaces it, so submitting mid-upload would send an attachment
// reference that resolves to nothing.
export const areTemporaryAttachmentsReady = (state: StoreType) =>
	getTemporaryAttachments(state).every(
		(hashName) => getAttachmentStatus(state, hashName) === AsyncActionStatus.READY
	);

export const getAiGeneratedImagesRemainingLimit = (state: StoreType) =>
	state.aiAssistant.conversations.images?.remainingLimit?.data ?? null;

export const getAiGeneratedImagesRemainingLimitStatus = (state: StoreType) =>
	state.aiAssistant.conversations.images?.remainingLimit?.status ?? null;

export const getImagesAvailableAgainAt = (state: StoreType) =>
	state.aiAssistant.conversations.images?.availableAgainAt ?? null;

export const getPromptsRemainingLimit = (state: StoreType) =>
	state.aiAssistant.conversations.prompts?.remainingLimit?.data ?? null;

export const getPromptsAvailableAgainAt = (state: StoreType) =>
	state.aiAssistant.conversations.prompts?.availableAgainAt ?? null;

export const getLastGeneratedImageUrl = (state: StoreType) =>
	state.aiAssistant.conversations.images?.lastGeneratedImageUrl ?? null;

export const getLastGeneratedArtifactId = (state: StoreType) =>
	state.aiAssistant.conversations.lastGeneratedArtifactId ?? null;

export const getGeneratedDocumentSync = (state: StoreType) =>
	state.aiAssistant.conversations.documents.sync;

export const getArtifactsLoadingStatus = (state: StoreType) => {
	const pages = state.aiAssistant.artifacts.pages;

	if (pages.length === 0) {
		return AiLoadingStatus.NONE;
	}
	if (pages[0].status === AsyncActionStatus.LOADING) {
		return AiLoadingStatus.INITIAL;
	}
	if (pages[pages.length - 1].status === AsyncActionStatus.LOADING) {
		return AiLoadingStatus.PAGINATION;
	}
	return AiLoadingStatus.LOADED;
};

export const getGeneratedArtifactsData = (state: StoreType): GeneratedArtifact[] =>
	state.aiAssistant.artifacts.pages.reduce((all, page) => {
		if (page.data) {
			return all.concat(page.data);
		}
		return all;
	}, [] as GeneratedArtifact[]);

export const getGeneratedArtifactsHasNext = (state: StoreType) =>
	state.aiAssistant.artifacts.pagination.hasNext;
